import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Flex, Select, Spin } from 'antd';

import FormFieldLabel from '@totem/components/ui_components/forms/FormFieldLabel';
import OrganizationContext from '@totem/components/userContext/organizationContext/organizationContext';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { ObjectReference } from '@totem/types/common';
import { OrganizationHierarchy } from '@totem/types/organization';
import { UserRestrictions } from '@totem/types/user';
import { isNull } from '@totem/utilities/common';
import { ORGANIZATION_HIERARCHY_ENDPOINT } from '@totem/utilities/endpoints';
import { sortStringAscending } from '@totem/utilities/tableUtilities';

const EMPTY_REF: ObjectReference = { id: '', name: '' };

const EMPTY_ID = '000000000000000000000000';

type Props = {
  showLabels?: boolean;
  labelPosition?: string;
  onChange: (
    organization: ObjectReference,
    region: ObjectReference,
    building: ObjectReference,
    controlSystem: ObjectReference,
  ) => void;
  defaultOrganizationId?: string;
  defaultRegionId?: string;
  defaultBuildingId?: string;
  defaultControlSystemId?: string;
  showOrganization?: boolean;
  showRegions?: boolean;
  showBuildings?: boolean;
  showControlSystems?: boolean;
  allowUnassignedOrganization?: boolean;
  allowUnassignedBuilding?: boolean;
};

const OrgHierarchySelector = ({
  onChange,
  defaultOrganizationId,
  defaultRegionId,
  defaultBuildingId,
  defaultControlSystemId,
  showOrganization = false,
  showRegions = false,
  showBuildings = true,
  showControlSystems = false,
  showLabels = false,
  labelPosition = 'top',
  allowUnassignedOrganization = false,
  allowUnassignedBuilding = false,
}: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const { data: currentOrganizationHierarchy } =
    useContext(OrganizationContext);

  const defaultsApplied = useRef(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hierarchyData, setHierarchyData] =
    useState<OrganizationHierarchy>(null);
  const [selectedOrganization, setSelectedOrganization] =
    useState<ObjectReference>(() => {
      // An explicit EMPTY_ID means the caller knows the record has no
      // organization assigned and wants that reflected (vs. `undefined`,
      // which means "no preference" and keeps the old default-to-current-org
      // behavior other callers rely on).
      if (defaultOrganizationId === EMPTY_ID) {
        return { id: EMPTY_ID, name: '' };
      }

      const defaultOrg = defaultOrganizationId
        ? userProfile.organizations?.find(
            (item) => item.id === defaultOrganizationId,
          )
        : null;

      return defaultOrg
        ? { id: defaultOrg.id, name: defaultOrg.name }
        : {
            id: userProfile.organization.id,
            name: userProfile.organization.name,
          };
    });
  const [selectedRegion, setSelectedRegion] = useState<ObjectReference>(null);
  const [selectedBuilding, setSelectedBuilding] =
    useState<ObjectReference>(null);
  const [selectedControlSystem, setSelectedControlSystem] =
    useState<ObjectReference>(null);

  const notifyChange = useCallback(
    (
      org: ObjectReference,
      region: ObjectReference | null,
      building: ObjectReference | null,
      controlSystem: ObjectReference | null,
    ) => {
      onChange(
        org ?? EMPTY_REF,
        region ?? EMPTY_REF,
        building ?? EMPTY_REF,
        controlSystem ?? EMPTY_REF,
      );
    },
    [onChange],
  );

  // Apply default selections once after hierarchy data is first available
  useEffect(() => {
    if (!hierarchyData?.children || defaultsApplied.current) return;
    if (!defaultRegionId && !defaultBuildingId && !defaultControlSystemId)
      return;

    defaultsApplied.current = true;

    let resolvedRegion: ObjectReference | null = null;
    let resolvedBuilding: ObjectReference | null = null;
    let resolvedControlSystem: ObjectReference | null = null;

    // Resolve building and auto-detect its parent region
    if (defaultBuildingId) {
      for (const rgn of hierarchyData.children) {
        const bld = rgn.children?.find((item) => item.id === defaultBuildingId);

        if (bld) {
          resolvedBuilding = { id: bld.id, name: bld.name };

          resolvedRegion = { id: rgn.id, name: rgn.name };

          break;
        }
      }
    }

    // Resolve region if not already set from the building lookup
    if (defaultRegionId && !resolvedRegion) {
      const rgn = hierarchyData.children.find(
        (item) => item.id === defaultRegionId,
      );

      if (rgn) resolvedRegion = { id: rgn.id, name: rgn.name };
    }

    // Resolve control system, auto-detecting building and region if needed
    if (defaultControlSystemId) {
      let found = false;

      for (const rgn of hierarchyData.children) {
        if (found) break;

        for (const bld of rgn.children ?? []) {
          const sys = bld.children?.find(
            (item) => item.id === defaultControlSystemId,
          );

          if (sys) {
            resolvedControlSystem = { id: sys.id, name: sys.name };
            if (!resolvedBuilding)
              resolvedBuilding = { id: bld.id, name: bld.name };
            if (!resolvedRegion)
              resolvedRegion = { id: rgn.id, name: rgn.name };

            found = true;
            break;
          }
        }
      }
    }

    if (resolvedRegion) setSelectedRegion(resolvedRegion);
    if (resolvedBuilding) setSelectedBuilding(resolvedBuilding);
    if (resolvedControlSystem) setSelectedControlSystem(resolvedControlSystem);

    if (resolvedRegion || resolvedBuilding || resolvedControlSystem) {
      notifyChange(
        selectedOrganization,
        resolvedRegion,
        resolvedBuilding,
        resolvedControlSystem,
      );
    }
  }, [
    hierarchyData,
    defaultRegionId,
    defaultBuildingId,
    defaultControlSystemId,
    notifyChange,
    selectedOrganization,
  ]);

  // Use current org hierarchy from context when it's the active org
  useEffect(() => {
    if (selectedOrganization.id === userProfile.organization.id) {
      setHierarchyData(currentOrganizationHierarchy);
    }
  }, [
    selectedOrganization.id,
    userProfile.organization.id,
    currentOrganizationHierarchy,
  ]);

  // Fetch hierarchy for a non-current org
  useEffect(() => {
    if (selectedOrganization.id === userProfile.organization.id) return;

    setIsLoading(true);

    fetch(`${ORGANIZATION_HIERARCHY_ENDPOINT}/${selectedOrganization.id}`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => res.json())
      .then((result: UserRestrictions) => {
        setHierarchyData(result.hierarchy);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [selectedOrganization.id, userProfile.organization.id, getApiToken]);

  const availableRegions = useMemo<OrganizationHierarchy[]>(() => {
    if (!hierarchyData?.children) return [];
    return [...hierarchyData.children].sort((chkA, chkB) =>
      sortStringAscending(chkA.name, chkB.name),
    );
  }, [hierarchyData]);

  const availableBuildings = useMemo<OrganizationHierarchy[]>(() => {
    if (!hierarchyData?.children) return [];

    if (showRegions) {
      if (!selectedRegion) return [];
      const regionNode = hierarchyData.children.find(
        (rgn) => rgn.id === selectedRegion.id,
      );
      return [...(regionNode?.children ?? [])].sort((chkA, chkB) =>
        sortStringAscending(chkA.name, chkB.name),
      );
    }

    // showRegions is false — show all buildings across all regions
    const all: OrganizationHierarchy[] = [];
    for (const rgn of hierarchyData.children) {
      if (rgn.children) all.push(...rgn.children);
    }
    return all.sort((chkA, chkB) => sortStringAscending(chkA.name, chkB.name));
  }, [hierarchyData, showRegions, selectedRegion]);

  const availableControlSystems = useMemo<OrganizationHierarchy[]>(() => {
    if (!hierarchyData?.children) return [];

    if (showBuildings) {
      if (!selectedBuilding) return [];
      for (const rgn of hierarchyData.children) {
        const buildingNode = rgn.children?.find(
          (bld) => bld.id === selectedBuilding.id,
        );
        if (buildingNode) {
          return [...(buildingNode.children ?? [])].sort((chkA, chkB) =>
            sortStringAscending(chkA.name, chkB.name),
          );
        }
      }
      return [];
    }

    // showBuildings is false — show all control systems across the org
    const all: OrganizationHierarchy[] = [];
    for (const rgn of hierarchyData.children) {
      for (const bld of rgn.children ?? []) {
        if (bld.children) all.push(...bld.children);
      }
    }
    return all.sort((chkA, chkB) => sortStringAscending(chkA.name, chkB.name));
  }, [hierarchyData, showBuildings, selectedBuilding]);

  const findRegionForBuilding = useCallback(
    (buildingId: string): ObjectReference | null => {
      if (!hierarchyData?.children) return null;
      for (const rgn of hierarchyData.children) {
        if (rgn.children?.find((bld) => bld.id === buildingId)) {
          return { id: rgn.id, name: rgn.name };
        }
      }
      return null;
    },
    [hierarchyData],
  );

  const handleOrgChange = (orgId: string) => {
    if (orgId === EMPTY_ID) {
      const unassignedRef: ObjectReference = { id: EMPTY_ID, name: '' };
      setSelectedOrganization(unassignedRef);

      setSelectedRegion(null);

      setSelectedBuilding(null);

      setSelectedControlSystem(null);

      notifyChange(unassignedRef, null, null, null);
      return;
    }
    const org = userProfile.organizations.find((item) => item.id === orgId);
    if (!org) return;
    const orgRef: ObjectReference = { id: org.id, name: org.name };
    setSelectedOrganization(orgRef);

    setSelectedRegion(null);

    setSelectedBuilding(null);

    setSelectedControlSystem(null);

    notifyChange(orgRef, null, null, null);
  };

  const handleRegionChange = (regionId: string) => {
    const rgn = availableRegions.find((item) => item.id === regionId);
    if (!rgn) return;
    const regionRef: ObjectReference = { id: rgn.id, name: rgn.name };
    setSelectedRegion(regionRef);

    setSelectedBuilding(null);

    setSelectedControlSystem(null);

    notifyChange(selectedOrganization, regionRef, null, null);
  };

  const handleBuildingChange = (buildingId: string) => {
    const bld = availableBuildings.find((item) => item.id === buildingId);
    if (!bld) return;
    const buildingRef: ObjectReference = { id: bld.id, name: bld.name };

    // When regions are hidden, auto-detect the region from the hierarchy
    const regionRef = showRegions
      ? selectedRegion
      : findRegionForBuilding(buildingId);
    if (!showRegions) setSelectedRegion(regionRef);

    setSelectedBuilding(buildingRef);

    setSelectedControlSystem(null);

    notifyChange(selectedOrganization, regionRef, buildingRef, null);
  };

  const handleControlSystemChange = (csId: string) => {
    const sys = availableControlSystems.find((item) => item.id === csId);
    if (!sys) return;
    const csRef: ObjectReference = { id: sys.id, name: sys.name };
    setSelectedControlSystem(csRef);

    notifyChange(selectedOrganization, selectedRegion, selectedBuilding, csRef);
  };

  useEffect(() => {
    if (isNull(selectedOrganization)) {
      setSelectedOrganization({
        id: userProfile.organization.id,
        name: selectedOrganization.name,
      });
    }

    handleOrgChange(selectedOrganization.id);
  }, [userProfile.organization.id]);

  const orgOptions = [
    ...(allowUnassignedOrganization
      ? [{ value: EMPTY_ID, label: 'Unassigned' }]
      : []),
    ...[...(userProfile.organizations ?? [])]
      .filter((org) => org.isActive)
      .sort((chkA, chkB) => sortStringAscending(chkA.name, chkB.name))
      .map((org) => ({ value: org.id, label: org.name })),
  ];

  return (
    <Spin spinning={isLoading}>
      <Flex vertical style={{ width: '100%' }} gap="small">
        {showOrganization && (
          <>
            {showLabels && labelPosition === 'top' && (
              <FormFieldLabel>Organization:</FormFieldLabel>
            )}
            {showLabels && labelPosition === 'left' && (
              <span>Organization:</span>
            )}
            <Select
              style={{ width: '100%' }}
              placeholder="Select Organization"
              value={selectedOrganization.id}
              options={orgOptions}
              onChange={handleOrgChange}
            />
          </>
        )}
        {showRegions && (
          <>
            {showLabels && labelPosition === 'top' && (
              <FormFieldLabel>Region:</FormFieldLabel>
            )}
            {showLabels && labelPosition === 'left' && <span>Region:</span>}
            <Select
              style={{ width: '100%' }}
              placeholder="Select Region"
              value={selectedRegion?.id ?? null}
              options={availableRegions.map((rgn) => ({
                value: rgn.id,
                label: rgn.name,
              }))}
              onChange={handleRegionChange}
              disabled={availableRegions.length === 0}
              allowClear
              onClear={() => {
                setSelectedRegion(null);

                setSelectedBuilding(null);

                setSelectedControlSystem(null);

                notifyChange(selectedOrganization, null, null, null);
              }}
            />
          </>
        )}
        {showBuildings && (
          <>
            {showLabels && labelPosition === 'top' && (
              <FormFieldLabel>Building:</FormFieldLabel>
            )}
            {showLabels && labelPosition === 'left' && <span>Building:</span>}
            <Select
              style={{ width: '100%' }}
              placeholder="Select Building"
              value={selectedBuilding?.id ?? null}
              options={[
                ...(allowUnassignedBuilding
                  ? [{ value: EMPTY_ID, label: 'Unassigned' }]
                  : []),
                ...availableBuildings.map((bld) => ({
                  value: bld.id,
                  label: bld.name,
                })),
              ]}
              onChange={(bldId) => {
                if (bldId === EMPTY_ID) {
                  setSelectedBuilding({ id: EMPTY_ID, name: '' });

                  setSelectedControlSystem(null);

                  notifyChange(
                    selectedOrganization,
                    selectedRegion,
                    { id: EMPTY_ID, name: '' },
                    null,
                  );
                } else {
                  handleBuildingChange(bldId);
                }
              }}
              disabled={
                !allowUnassignedBuilding && availableBuildings.length === 0
              }
              allowClear
              onClear={() => {
                setSelectedBuilding(null);

                setSelectedControlSystem(null);

                notifyChange(selectedOrganization, selectedRegion, null, null);
              }}
            />
          </>
        )}
        {showControlSystems && (
          <>
            {showLabels && labelPosition === 'top' && (
              <FormFieldLabel>Control System:</FormFieldLabel>
            )}
            {showLabels && labelPosition === 'left' && (
              <span>Control System:</span>
            )}
            <Select
              style={{ width: '100%' }}
              placeholder="Select Control System"
              value={selectedControlSystem?.id ?? null}
              options={availableControlSystems.map((sys) => ({
                value: sys.id,
                label: sys.name,
              }))}
              onChange={handleControlSystemChange}
              disabled={availableControlSystems.length === 0}
              allowClear
              onClear={() => {
                setSelectedControlSystem(null);

                notifyChange(
                  selectedOrganization,
                  selectedRegion,
                  selectedBuilding,
                  null,
                );
              }}
            />
          </>
        )}
      </Flex>
    </Spin>
  );
};

export default OrgHierarchySelector;
